/**
 * Pure JsonLogic evaluator for the curated 14-operator subset declared in
 * `@ethisyscore/protocol`'s {@link KNOWN_OPERATORS}.
 *
 * The evaluator implements **only** the closed vocabulary:
 *
 * - Comparison: `==`, `!=`, `>`, `<`, `>=`, `<=`
 * - Boolean:    `and`, `or`, `not`
 * - Arithmetic: `+`, `-`, `*`, `/`, `%`
 * - Reference:  `var` (dot-path resolution against the supplied context)
 *
 * Design constraints:
 *
 * - **Pure function.** No I/O, no async, no `eval`, no `Function` construction.
 * - **Closed vocabulary.** Operators outside {@link KNOWN_OPERATORS} cause an
 *   immediate throw — there is no extension mechanism for plugin authors.
 * - **No function values.** Function-typed inputs are rejected at every depth.
 *   This keeps expression trees serialisable and free of host-side side effects.
 * - **Fail-loud.** Malformed expressions (e.g. an operator object with more or
 *   fewer than one key) throw rather than silently returning `undefined`.
 */
import { KNOWN_OPERATORS } from "@ethisyscore/protocol";

/**
 * Resolution context for `var` references. Keys are looked up via dot-path
 * traversal; missing paths resolve to `undefined`.
 */
export type EvaluatorContext = Record<string, unknown>;

const KNOWN_OPERATOR_SET = new Set<string>(KNOWN_OPERATORS);

/**
 * Evaluate a JsonLogic expression in the closed operator subset against the
 * supplied context.
 *
 * @param expr    The expression tree. Scalars (`string | number | boolean | null`)
 *                evaluate to themselves; arrays map element-wise; operator
 *                objects must contain exactly one key from
 *                {@link KNOWN_OPERATORS}.
 * @param context The context bag for `var` resolution. Dot-paths traverse
 *                nested objects.
 *
 * @throws Error if `expr` contains an unknown operator, a function-typed value,
 *         an operator object with a number of keys other than one, or any
 *         other unsupported shape (e.g. `undefined`, `Symbol`, `bigint`).
 */
export function evaluate(expr: unknown, context: EvaluatorContext): unknown
{
    // Literal scalars — return as-is.
    if (expr === null)
    {
        return null;
    }

    const t = typeof expr;
    if (t === "string" || t === "number" || t === "boolean")
    {
        return expr;
    }

    if (t === "function")
    {
        throw new Error("Function-typed values are not allowed in expressions.");
    }

    if (Array.isArray(expr))
    {
        return expr.map((e) => evaluate(e, context));
    }

    if (t !== "object")
    {
        throw new Error(`Unsupported expression type: ${t}.`);
    }

    // Operator object — must have exactly one key from the closed vocabulary.
    const obj = expr as Record<string, unknown>;
    const keys = Object.keys(obj);
    if (keys.length !== 1)
    {
        throw new Error(
            `Expression object must have exactly one operator key (got ${keys.length}).`,
        );
    }

    const op = keys[0];
    if (!KNOWN_OPERATOR_SET.has(op))
    {
        throw new Error(`Unknown operator: ${op}.`);
    }

    const rawArgs = obj[op];

    // `var` takes a single dot-path argument and does not recurse over its operand.
    if (op === "var")
    {
        const path = typeof rawArgs === "string"
            ? rawArgs
            : Array.isArray(rawArgs) && typeof rawArgs[0] === "string"
                ? rawArgs[0]
                : "";
        return resolveVar(path, context);
    }

    const evaluatedArgs: unknown[] = Array.isArray(rawArgs)
        ? rawArgs.map((a) => evaluate(a, context))
        : [evaluate(rawArgs, context)];

    switch (op)
    {
        case "==": return evaluatedArgs[0] === evaluatedArgs[1];
        case "!=": return evaluatedArgs[0] !== evaluatedArgs[1];
        case ">":  return (evaluatedArgs[0] as number) >  (evaluatedArgs[1] as number);
        case "<":  return (evaluatedArgs[0] as number) <  (evaluatedArgs[1] as number);
        case ">=": return (evaluatedArgs[0] as number) >= (evaluatedArgs[1] as number);
        case "<=": return (evaluatedArgs[0] as number) <= (evaluatedArgs[1] as number);
        case "and": return evaluatedArgs.every(Boolean);
        case "or":  return evaluatedArgs.some(Boolean);
        case "not": return !evaluatedArgs[0];
        case "+":   return evaluatedArgs.reduce((acc: number, n) => acc + (n as number), 0);
        case "-":   return (evaluatedArgs[0] as number) - (evaluatedArgs[1] as number);
        case "*":   return evaluatedArgs.reduce((acc: number, n) => acc * (n as number), 1);
        case "/":   return safeDivide(evaluatedArgs[0] as number, evaluatedArgs[1] as number);
        case "%":   return safeModulo(evaluatedArgs[0] as number, evaluatedArgs[1] as number);
        default:
            // Unreachable: gated by KNOWN_OPERATOR_SET above. Kept for exhaustiveness.
            throw new Error(`Unknown operator: ${op}.`);
    }
}

/**
 * Throw on zero or non-finite denominators in `/`. JavaScript silently coerces
 * `1 / 0` to `Infinity`; that value then leaks into host-rendered props and
 * surfaces as weird UI (or React `<input value={Infinity} />` warnings). The
 * closed evaluator's fail-loud contract demands a real error so the host
 * surface mount can fall back to an `ErrorState`.
 */
function safeDivide(a: number, b: number): number
{
    if (b === 0 || !Number.isFinite(b))
    {
        throw new Error(
            `Division by ${b === 0 ? "zero" : String(b)} is not allowed in the closed evaluator.`,
        );
    }
    return a / b;
}

/**
 * Throw on zero or non-finite denominators in `%`. JavaScript silently coerces
 * `1 % 0` to `NaN`; that value would compare unequal to itself and corrupt
 * downstream comparisons in reactive rules.
 */
function safeModulo(a: number, b: number): number
{
    if (b === 0 || !Number.isFinite(b))
    {
        throw new Error(
            `Modulo by ${b === 0 ? "zero" : String(b)} is not allowed in the closed evaluator.`,
        );
    }
    return a % b;
}

/**
 * Resolve a dot-separated path against the supplied context, returning the
 * leaf value or `undefined` if any segment is missing or non-traversable.
 *
 * Empty paths resolve to `undefined`.
 */
function resolveVar(path: string, context: EvaluatorContext): unknown
{
    if (path === "")
    {
        return undefined;
    }

    const parts = path.split(".");
    let curr: unknown = context;
    for (const part of parts)
    {
        if (curr === null || typeof curr !== "object")
        {
            return undefined;
        }
        curr = (curr as Record<string, unknown>)[part];
    }
    return curr;
}
